import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import StatusBadge from '../../components/shared/StatusBadge';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { ShoppingCart, Users, Package, TrendingUp, AlertCircle, BarChart2, Award, Tag, MessageCircle } from 'lucide-react';
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';

function KPICard({ label, value, icon: Icon, color, sub }) {
  return (
    <div className="card-dashboard" data-testid="kpi-card">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" strokeWidth={1.8} />
        </div>
      </div>
      <p className="font-dashboard font-bold text-2xl text-brand-text">{value}</p>
      <p className="font-body text-sm text-brand-text-muted mt-0.5">{label}</p>
      {sub && <p className="font-body text-xs text-brand-text-muted mt-1">{sub}</p>}
    </div>
  );
}

const PERIOD_TABS = [
  { label: '1M', value: 1 },
  { label: '3M', value: 3 },
  { label: '6M', value: 6 },
  { label: '9M', value: 9 },
  { label: '12M', value: 12 },
];

const FatTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-white border border-brand-border rounded-lg px-3 py-2 shadow-lg text-xs font-body">
        <p className="font-dashboard font-semibold text-brand-text mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>
            {p.name === 'faturamento' ? formatCurrency(p.value) : `${p.value} pedidos`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function DashboardHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ pendentes: 0, hoje: 0, producao: 0, clientes_pendentes: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(6);
  const [chartData, setChartData] = useState({ meses: [], top_produtos: [] });
  const [chartLoading, setChartLoading] = useState(false);
  const isAdmin = user?.role === 'admin';

  // Gráfico por categoria
  const today = new Date();
  const firstDay = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const lastDayStr = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
  const [catInicio, setCatInicio] = useState(firstDay);
  const [catFim, setCatFim] = useState(lastDayStr);
  const [catData, setCatData] = useState([]);
  const [catLoading, setCatLoading] = useState(false);

  // Clientes sem compra
  const [semCompraData, setSemCompraData] = useState(null);

  useEffect(() => {
    const todayStr = new Date().toISOString().split('T')[0];
    Promise.all([
      api.get(`/pedidos?status=Recebido&per_page=100`),
      api.get(`/pedidos?data_inicio=${todayStr}&data_fim=${todayStr}&per_page=100`),
      api.get(`/pedidos?status=Em Produção&per_page=100`),
      api.get('/clientes?status=Pendente'),
      api.get(`/pedidos?per_page=10`),
    ]).then(([pendentes, hoje, producao, clientesPend, recent]) => {
      setStats({
        pendentes: pendentes.data.total || 0,
        hoje: hoje.data.total || 0,
        producao: producao.data.total || 0,
        clientes_pendentes: clientesPend.data.length || 0,
      });
      setRecentOrders(recent.data.pedidos || []);
    }).catch(() => {}).finally(() => setLoading(false));

    // Clientes sem compra
    api.get('/clientes/stats-compra-mes').then(r => setSemCompraData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setChartLoading(true);
    api.get(`/pedidos/dashboard-stats?meses=${period}`)
      .then(r => setChartData(r.data))
      .catch(() => {})
      .finally(() => setChartLoading(false));
  }, [period]);

  const loadCatData = () => {
    if (!catInicio || !catFim) return;
    setCatLoading(true);
    api.get(`/pedidos/por-categoria?data_inicio=${catInicio}&data_fim=${catFim}`)
      .then(r => setCatData(r.data || []))
      .catch(() => {})
      .finally(() => setCatLoading(false));
  };

  useEffect(() => { loadCatData(); }, [catInicio, catFim]);

  return (
    <div data-testid="dashboard-home">
      <div className="mb-6">
        <h1 className="dashboard-heading text-2xl">Bom dia, {user?.nome?.split(' ')[0]}</h1>
        <p className="text-brand-text-muted font-body text-sm mt-1">Visão geral do sistema</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard label="Pedidos Recebidos" value={stats.pendentes} icon={ShoppingCart} color="bg-brand-primary" />
        <KPICard label="Pedidos Hoje" value={stats.hoje} icon={TrendingUp} color="bg-brand-dark" />
        <KPICard label="Em Produção" value={stats.producao} icon={Package} color="bg-amber-500" />
        <KPICard label="Clientes Pendentes" value={stats.clientes_pendentes} icon={Users} color={stats.clientes_pendentes > 0 ? "bg-red-500" : "bg-emerald-500"} sub={stats.clientes_pendentes > 0 ? "Aguardando aprovação" : "Tudo em dia"} />
      </div>

      {/* Alerts */}
      {stats.clientes_pendentes > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 flex items-center gap-3" data-testid="alert-clientes-pendentes">
          <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-dashboard font-semibold text-sm text-orange-800">{stats.clientes_pendentes} cadastro(s) aguardando aprovação</p>
          </div>
          <Link to="/admin/clientes?status=Pendente" className="text-sm text-orange-700 font-dashboard font-semibold hover:underline">Ver</Link>
        </div>
      )}

      {/* Charts Section */}
      <div className="card-dashboard mb-6" data-testid="dashboard-charts">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-brand-primary" />
            <h2 className="dashboard-heading text-base">Desempenho</h2>
          </div>
          {/* Period tabs */}
          <div className="flex gap-1 bg-brand-bg rounded-lg p-1">
            {PERIOD_TABS.map(t => (
              <button
                key={t.value}
                onClick={() => setPeriod(t.value)}
                className={`px-3 py-1 rounded-md text-xs font-dashboard font-semibold transition-all ${
                  period === t.value
                    ? 'bg-brand-primary text-white shadow-sm'
                    : 'text-brand-text-muted hover:text-brand-text'
                }`}
                data-testid={`period-tab-${t.label}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {chartLoading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className={`grid gap-6 ${isAdmin ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
            {/* Faturamento chart — admin only */}
            {isAdmin && (
              <div>
                <p className="text-xs font-dashboard font-semibold text-brand-text-muted mb-3 uppercase tracking-wide">Faturamento (R$)</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData.meses} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0ebe6" />
                    <XAxis dataKey="mes" tick={{ fontSize: 10, fill: '#7A6558' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#7A6558' }} axisLine={false} tickLine={false} tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} />
                    <Tooltip content={<FatTooltip />} />
                    <Bar dataKey="faturamento" name="faturamento" fill="#BB3E18" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Pedidos chart */}
            <div>
              <p className="text-xs font-dashboard font-semibold text-brand-text-muted mb-3 uppercase tracking-wide">Pedidos por mês</p>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={chartData.meses} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="pedidosGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#BB3E18" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#BB3E18" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0ebe6" />
                  <XAxis dataKey="mes" tick={{ fontSize: 10, fill: '#7A6558' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#7A6558' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<FatTooltip />} />
                  <Area type="monotone" dataKey="pedidos" name="pedidos" stroke="#BB3E18" strokeWidth={2} fill="url(#pedidosGrad)" dot={{ r: 3, fill: '#BB3E18' }} activeDot={{ r: 5 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Bottom grid: Recent orders + Top products */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent orders */}
        <div className="lg:col-span-2 card-dashboard">
          <div className="flex items-center justify-between mb-4">
            <h2 className="dashboard-heading text-base">Pedidos Recentes</h2>
            <Link to="/admin/pedidos" className="text-sm text-brand-primary font-dashboard hover:underline">Ver todos</Link>
          </div>
          {loading ? (
            <div className="flex justify-center py-8"><div className="w-6 h-6 border-3 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>
          ) : recentOrders.length === 0 ? (
            <p className="text-center text-brand-text-muted text-sm py-8 font-body">Nenhum pedido ainda</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-body">
                <thead>
                  <tr className="border-b border-brand-border">
                    <th className="text-left py-2 px-2 text-xs text-brand-text-muted font-dashboard">Pedido</th>
                    <th className="text-left py-2 px-2 text-xs text-brand-text-muted font-dashboard">Cliente</th>
                    <th className="text-left py-2 px-2 text-xs text-brand-text-muted font-dashboard">Status</th>
                    <th className="text-left py-2 px-2 text-xs text-brand-text-muted font-dashboard">Data</th>
                    {isAdmin && <th className="text-right py-2 px-2 text-xs text-brand-text-muted font-dashboard">Total</th>}
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map(p => (
                    <tr key={p.id} className="border-b border-brand-border last:border-0 hover:bg-brand-bg">
                      <td className="py-2.5 px-2">
                        <Link to={`/admin/pedidos/${p.id}`} className="text-brand-primary font-semibold hover:underline">#{p.numero}</Link>
                      </td>
                      <td className="py-2.5 px-2 text-brand-text truncate max-w-[120px]">{p.cliente_nome || '–'}</td>
                      <td className="py-2.5 px-2"><StatusBadge status={p.status} /></td>
                      <td className="py-2.5 px-2 text-brand-text-muted">{formatDate(p.data_pedido)}</td>
                      {isAdmin && <td className="py-2.5 px-2 text-right font-semibold text-brand-text">{p.total != null ? formatCurrency(p.total) : '–'}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Top Products */}
        <div className="card-dashboard">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-4 h-4 text-brand-primary" />
            <h2 className="dashboard-heading text-base">Top Produtos</h2>
          </div>
          {chartLoading ? (
            <div className="flex justify-center py-8"><div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>
          ) : chartData.top_produtos.length === 0 ? (
            <p className="text-center text-brand-text-muted text-sm py-8 font-body">Sem dados no período</p>
          ) : (
            <div className="space-y-3">
              {chartData.top_produtos.map((p, i) => {
                const max = chartData.top_produtos[0]?.quantidade_total || 1;
                const pct = Math.round((p.quantidade_total / max) * 100);
                return (
                  <div key={i} data-testid="top-produto-item">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-body text-brand-text truncate max-w-[150px]">
                        <span className="text-brand-text-muted mr-1.5 font-dashboard font-bold">{i + 1}.</span>
                        {p.descricao}
                      </p>
                      <p className="text-xs font-dashboard font-bold text-brand-primary ml-2 shrink-0">{p.quantidade_total}</p>
                    </div>
                    <div className="h-1.5 bg-brand-accent-light rounded-full overflow-hidden">
                      <div className="h-full bg-brand-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-xs text-brand-text-muted font-body mt-4 text-center">Últimos {period === 1 ? '30 dias' : `${period} meses`}</p>
        </div>
      </div>

      {/* === Pedidos por Categoria === */}
      <div className="card-dashboard mt-6" data-testid="chart-por-categoria">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-brand-primary" />
            <h2 className="dashboard-heading text-base">Pedidos por Categoria</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input type="date" value={catInicio} onChange={e => setCatInicio(e.target.value)}
              className="rounded-lg border border-brand-border px-3 py-1.5 text-sm font-body"
              data-testid="cat-date-inicio" />
            <span className="text-xs text-brand-text-muted">até</span>
            <input type="date" value={catFim} onChange={e => setCatFim(e.target.value)}
              className="rounded-lg border border-brand-border px-3 py-1.5 text-sm font-body"
              data-testid="cat-date-fim" />
          </div>
        </div>
        {catLoading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : catData.length === 0 ? (
          <p className="text-center text-brand-text-muted text-sm py-8 font-body">Nenhum pedido no período</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={catData} layout="vertical" margin={{ left: 16, right: 24, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => formatCurrency(v)} />
              <YAxis type="category" dataKey="categoria" width={130} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(v, name) => [
                  name === 'valor' ? formatCurrency(v) : `${v} un.`,
                  name === 'valor' ? 'Valor' : 'Qtd',
                ]}
              />
              <Bar dataKey="valor" name="valor" fill="#BB3E18" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* === Clientes sem compra no mês === */}
      {semCompraData && (
        <div className="card-dashboard mt-6" data-testid="card-sem-compra">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-brand-primary" />
            <h2 className="dashboard-heading text-base">Clientes sem Compra no Mês</h2>
            <span className="ml-auto text-xs bg-amber-100 text-amber-700 font-dashboard font-semibold px-2 py-0.5 rounded-full">
              {semCompraData.nao_compraram} cliente(s)
            </span>
          </div>
          {semCompraData.clientes_sem_compra.length === 0 ? (
            <p className="text-center text-brand-text-muted text-sm py-6 font-body">Todos os clientes já compraram este mês!</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {semCompraData.clientes_sem_compra.map(c => {
                const d = (c.whatsapp || '').replace(/\D/g, '');
                const wa = d ? `https://wa.me/${d.length <= 11 ? '55' + d : d}` : null;
                return (
                  <div key={c.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-brand-bg hover:bg-brand-accent-light transition-colors">
                    <span className="text-sm font-body text-brand-text truncate">{c.nome}</span>
                    {wa ? (
                      <a
                        href={wa}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-dashboard font-semibold hover:bg-green-200 transition-colors shrink-0 ml-3"
                        data-testid="wa-sem-compra-btn"
                      >
                        <MessageCircle className="w-3 h-3" /> WhatsApp
                      </a>
                    ) : (
                      <span className="text-xs text-brand-text-muted shrink-0 ml-3">Sem WhatsApp</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
